import { capitalize, get, orderBy } from 'lodash';
import { connect } from 'react-redux';
import i18next from 'i18next';
import React, { useRef, useState, useEffect } from 'react';

import ComponentLoader from '@commons/DeepsightComponents/ComponentLoader';
import Text, { ENUM_COLORS, ENUM_FONTS } from '@commons/Text';

import MessageItem from '../MessageItem';
import TypingIndicator from '../TypingIndicator';

import {
  EmptyStateContainer,
  EmptyStateIcon,
  EmptyStateTextContainer,
  SuggestionsContainer,
  SuggestionCard,
  SuggestionIcon,
  SuggestionText,
  MessageListContainer,
  TextContainer,
  LoadingContainer,
  EmptyStateIconContainer,
} from './styledComponents';

const PROCESSING_MESSAGE_STATUS = 'processing';

const MessageList = (props) => {
  const {
    messages,
    isAiTyping,
    handleFeedBack,
    handleRetryMessage,
    isMessagesLoading,
    isConversationLoading,
    user,
    sendMessage,
    handleExportTable,
    dailyUsage,
  } = props;

  const isAnyMessageInProgress = Object.values(messages).some(
    ({ status }) => status === PROCESSING_MESSAGE_STATUS,
  );

  const containerRef = useRef({ current: {} });

  const [sortedMessages, setSortedMessages] = useState([]);
  const [scrollHeight, setScrollHeight] = useState(0);

  const scrollToBottom = () => {
    if (containerRef.current) {
      containerRef.current.scrollTo({
        top: containerRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  };

  useEffect(() => {
    const updatedSortedMessages = orderBy(Object.values(messages), 'createdAt', 'asc');

    setSortedMessages(updatedSortedMessages);
  }, [messages, isAiTyping]);

  // This useEffect is used to scroll properly to the bottom of the container when the scroll height changes
  // (except for retry messages because it is not necessary at the end of the conversation)
  useEffect(() => {
    if (
      containerRef.current.scrollHeight &&
      containerRef.current.scrollHeight !== scrollHeight &&
      !isAnyMessageInProgress
    ) {
      setScrollHeight(containerRef.current.scrollHeight);
      scrollToBottom();
    }
  }, [containerRef.current.scrollHeight, isAnyMessageInProgress]);

  const renderEmptyState = () => {
    const suggestions = [
      {
        icon: '📋',
        text: i18next.t('AGENT.AGENT.SUGGESTION_1_TITLE'),
        action: () => {
          if (dailyUsage === 100) {
            return;
          }
          sendMessage(i18next.t('AGENT.AGENT.SUGGESTION_1_PROMPT'));
        },
      },
      {
        icon: '📊',
        text: i18next.t('AGENT.AGENT.SUGGESTION_2_TITLE'),
        action: () => {
          if (dailyUsage === 100) {
            return;
          }
          sendMessage(i18next.t('AGENT.AGENT.SUGGESTION_2_PROMPT'));
        },
      },
      {
        icon: '🚚',
        text: i18next.t('AGENT.AGENT.SUGGESTION_3_TITLE'),
        action: () => {
          if (dailyUsage === 100) {
            return;
          }
          sendMessage(i18next.t('AGENT.AGENT.SUGGESTION_3_PROMPT'));
        },
      },
    ];

    return (
      <EmptyStateContainer ref={containerRef}>
        {isMessagesLoading || isConversationLoading ? (
          <LoadingContainer>
            <ComponentLoader height={32} width={32} />
          </LoadingContainer>
        ) : (
          <>
            <EmptyStateIconContainer>
              <EmptyStateIcon src="/images/logo-inpulse.svg" />
            </EmptyStateIconContainer>
            <EmptyStateTextContainer>
              <Text color={ENUM_COLORS.DARKER} font={ENUM_FONTS.TEXT_MEDIUM_HEIGHT_18}>
                {i18next.t(
                  get(user, 'firstName', null)
                    ? 'AGENT.AGENT.HELLO_WITH_NAME'
                    : 'AGENT.AGENT.HELLO_WITHOUT_NAME',
                  { name: capitalize(get(user, 'firstName', '')) },
                )}
              </Text>
              <Text color={ENUM_COLORS.DGREY} font={ENUM_FONTS.TEXT_SMALL_HEIGHT_16}>
                {i18next.t('AGENT.AGENT.HELP_MESSAGE')}
              </Text>
            </EmptyStateTextContainer>

            <SuggestionsContainer>
              {suggestions.map((suggestion, index) => (
                <SuggestionCard
                  isDisabled={dailyUsage === 100}
                  key={index}
                  style={dailyUsage === 100 ? { opacity: 0.5 } : {}}
                  onClick={suggestion.action}
                >
                  <SuggestionIcon>
                    <span style={{ fontSize: '16px' }}>{suggestion.icon}</span>
                  </SuggestionIcon>
                  <SuggestionText>{suggestion.text}</SuggestionText>
                </SuggestionCard>
              ))}
            </SuggestionsContainer>
          </>
        )}
      </EmptyStateContainer>
    );
  };

  if (!sortedMessages.length) {
    return renderEmptyState();
  }

  return (
    <MessageListContainer ref={containerRef}>
      {sortedMessages.map((message) => (
        <MessageItem
          handleExportTable={handleExportTable}
          handleFeedBack={handleFeedBack}
          handleRetryMessage={handleRetryMessage}
          isRetryInProgress={isAnyMessageInProgress}
          key={message.id}
          message={message}
        />
      ))}
      {isAiTyping && <TypingIndicator />}
      {!!sortedMessages.length && (
        <TextContainer>
          <Text color={ENUM_COLORS.DGREY} font={ENUM_FONTS.TEXT_MICRO_HEIGHT_12}>
            {i18next.t('AGENT.AGENT.WARNING_MESSAGE')}
          </Text>
        </TextContainer>
      )}
    </MessageListContainer>
  );
};

const mapStateToProps = (state) => ({
  user: state.baseReducer.user,
});

export default connect(mapStateToProps)(MessageList);
